import { Link, useParams } from 'react-router';
import { api, formatDate } from '../api.ts';
import { Container } from '../components/Layout.tsx';
import { Alert, Card, CheckIcon, ErrorMessage, LinkButton, Loading, PageTitle } from '../components/ui.tsx';
import { useApi, useTitle } from '../hooks.ts';

interface Certificate {
  code: string;
  name: string;
  courseTitle: string;
  hours: number;
  issuedAt: string;
  verifyUrl: string;
}

export function CertificatesPage() {
  useTitle('Intyg');
  const { data, loading, error } = useApi<{ certificates: Certificate[] }>('/api/certificates');
  if (loading && !data) return <Loading />;
  return (
    <Container>
      <PageTitle sub="Intyget skapas automatiskt när du är klar med alla obligatoriska delar i kursen.">
        Intyg
      </PageTitle>
      <ErrorMessage error={error} />
      {data?.certificates.length === 0 && (
        <Card>
          <p>
            Du har inget intyg än. Fortsätt med kursen – när alla delar är klara kommer intyget här och i ett
            mejl.
          </p>
          <LinkButton to="/kurs" className="mt-4">
            Till kursen
          </LinkButton>
        </Card>
      )}
      <div className="space-y-5">
        {data?.certificates.map((c) => (
          <CertificateCard key={c.code} certificate={c} />
        ))}
      </div>
    </Container>
  );
}

function CertificateCard({ certificate: c }: { certificate: Certificate }) {
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-night-800 via-aurora-500 to-violet-aurora" />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-aurora-600">
            <CheckIcon className="h-4 w-4" /> Utfärdat {formatDate(c.issuedAt)}
          </p>
          <h2 className="mt-1 text-xl font-bold text-night-900">{c.courseTitle}</h2>
          <p className="mt-1">
            {c.name} · cirka {c.hours} timmar
          </p>
          <p className="mt-2 text-sm text-muted">
            Verifieringskod <strong className="font-mono">{c.code}</strong> ·{' '}
            <Link to={`/verifiera/${c.code}`} className="underline">
              verifieringssida
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={`/api/certificates/${c.code}/pdf`}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center rounded-lg bg-night-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-night-700"
          >
            Ladda ned PDF
          </a>
          <a
            href={`https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(c.courseTitle)}&organizationName=${encodeURIComponent('Luleå tekniska universitet')}&certUrl=${encodeURIComponent(c.verifyUrl)}&certId=${c.code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-night-800 ring-1 ring-night-800/20 hover:bg-night-100"
          >
            Lägg till på LinkedIn
          </a>
        </div>
      </div>
      <p className="mt-5 border-t border-night-100 pt-4 text-sm text-muted">
        Är namnet fel stavat?{' '}
        <Link to="/kontakt" className="font-semibold text-night-700 underline">
          Kontakta kursens lärare
        </Link>{' '}
        så rättar de intyget.
      </p>
    </Card>
  );
}

export function VerifyCertificatePage() {
  const { code } = useParams();
  useTitle('Verifiera intyg');
  const { data, loading } = useApi<{
    valid: boolean;
    name?: string;
    courseTitle?: string;
    hours?: number;
    issuedAt?: string;
    organisation?: string;
  }>(`/api/verify/${encodeURIComponent(code ?? '')}`);
  if (loading && !data) return <Loading />;
  return (
    <Container narrow>
      <PageTitle>Verifiering av intyg</PageTitle>
      {data?.valid ? (
        <Card className="space-y-2">
          <Alert kind="success">Intyget är äkta och utfärdat av {data.organisation}.</Alert>
          <dl className="mt-4 grid grid-cols-[8rem_1fr] gap-y-2">
            <dt className="text-muted">Namn</dt>
            <dd className="font-semibold">{data.name}</dd>
            <dt className="text-muted">Kurs</dt>
            <dd>{data.courseTitle}</dd>
            <dt className="text-muted">Omfattning</dt>
            <dd>cirka {data.hours} timmar</dd>
            <dt className="text-muted">Utfärdat</dt>
            <dd>{formatDate(data.issuedAt)}</dd>
            <dt className="text-muted">Kod</dt>
            <dd className="font-mono">{code}</dd>
          </dl>
        </Card>
      ) : (
        <Alert>Det finns inget intyg med koden {code}. Kontrollera att koden är rätt skriven.</Alert>
      )}
    </Container>
  );
}
